import type { Expense } from './domains/types';

/**
 * Export options for generating data exports
 */
export interface ExportOptions {
    format: 'csv' | 'pdf';
    dateRange?: {
        start: string; // ISO date string
        end: string;   // ISO date string
    };
    categories?: string[]; // Filter by category IDs
    currency?: string; // Convert all to one currency (future)
}

/**
 * Result of an export operation
 */
export interface ExportResult {
    filename: string;
    recordCount: number;
    fileSize: number;
    format: string;
}

/**
 * Service for exporting expense data to various formats
 * 
 * Currently supports:
 * - CSV export (RFC 4180 compliant)
 * 
 * Future: PDF export with formatted tables
 */
export class ExportService {
    /**
     * Export expenses to CSV format
     * 
     * CSV format follows RFC 4180 specification:
     * - Double quotes for field escaping
     * - Double-double quotes ("") for literal quotes
     * - UTF-8 encoding with BOM for Excel compatibility
     * 
     * @param expenses - Array of expenses to export
     * @param filename - Optional custom filename (default: expenses-YYYY-MM-DD.csv)
     * @returns Export result with metadata
     * 
     * @example
     * ```typescript
     * const result = ExportService.exportToCSV(expenses);
     * console.log(`Exported ${result.recordCount} expenses to ${result.filename}`);
     * ```
     */
    static exportToCSV(
        expenses: Expense[],
        filename?: string
    ): ExportResult {
        // Generate filename with current date
        const defaultFilename = `expenses-${new Date().toISOString().split('T')[0]}.csv`;
        const finalFilename = filename || defaultFilename;

        // CSV Header
        const headers = [
            'Date',
            'Amount',
            'Currency',
            'Category',
            'Description',
            'Created At',
            'Updated At'
        ];

        // Map expenses to CSV rows
        const rows = expenses.map(expense => [
            expense.date,
            expense.amount.toString(),
            expense.currency,
            this.escapeCsvField(expense.category || ''),
            this.escapeCsvField(expense.description || ''),
            expense.createdAt,
            expense.updatedAt || ''
        ]);

        // Generate CSV content
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => this.formatCsvField(cell)).join(','))
        ].join('\n');

        // Add UTF-8 BOM for Excel compatibility
        const BOM = '\ufeff';
        const csvWithBOM = BOM + csvContent;

        // Calculate file size (in bytes)
        const fileSize = new Blob([csvWithBOM]).size;

        // Trigger browser download
        this.downloadFile(csvWithBOM, finalFilename, 'text/csv;charset=utf-8;');

        return {
            filename: finalFilename,
            recordCount: expenses.length,
            fileSize,
            format: 'csv'
        };
    }

    /**
     * Export expenses with date range filter
     * 
     * @param expenses - Array of all expenses
     * @param startDate - Start date (ISO format: YYYY-MM-DD)
     * @param endDate - End date (ISO format: YYYY-MM-DD)
     * @param filename - Optional custom filename
     * @returns Export result
     */
    static exportToCSVWithDateRange(
        expenses: Expense[],
        startDate: string,
        endDate: string,
        filename?: string
    ): ExportResult {
        const filtered = expenses.filter(expense => {
            return expense.date >= startDate && expense.date <= endDate;
        });

        const defaultFilename = `expenses-${startDate}-to-${endDate}.csv`;
        return this.exportToCSV(filtered, filename || defaultFilename);
    }

    /**
     * Export expenses for current month
     * 
     * @param expenses - Array of all expenses
     * @returns Export result
     */
    static exportCurrentMonth(expenses: Expense[]): ExportResult {
        const now = new Date();
        const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);

        const start = this.formatLocalDate(startDate);
        const end = this.formatLocalDate(endDate);

        return this.exportToCSVWithDateRange(
            expenses,
            start,
            end,
            `expenses-${now.toLocaleString('en-US', { month: 'long', year: 'numeric' }).replace(' ', '-')}.csv`
        );
    }

    /**
     * Export expenses for current year
     * 
     * @param expenses - Array of all expenses
     * @returns Export result
     */
    static exportCurrentYear(expenses: Expense[]): ExportResult {
        const now = new Date();
        const startDate = new Date(now.getFullYear(), 0, 1);
        const endDate = new Date(now.getFullYear(), 11, 31);

        const start = this.formatLocalDate(startDate);
        const end = this.formatLocalDate(endDate);

        return this.exportToCSVWithDateRange(
            expenses,
            start,
            end,
            `expenses-${now.getFullYear()}.csv`
        );
    }

    /**
     * Escape special characters in CSV field according to RFC 4180
     * 
     * Rules:
     * - If field contains comma, quote, or newline → wrap in quotes
     * - If field contains quotes → double them (" → "")
     * 
     * @param field - Field value to escape
     * @returns Escaped field value
     */
    private static escapeCsvField(field: string): string {
        if (!field) return '';

        // Replace double quotes with double-double quotes
        return field.replace(/"/g, '""');
    }

    /**
     * Format CSV field for output (add quotes if needed)
     * 
     * @param field - Field value
     * @returns Formatted field (quoted if necessary)
     */
    private static formatCsvField(field: string): string {
        if (!field) return '""';

        // Check if field needs quoting (contains comma, quote, or newline)
        const needsQuoting = /[",\n\r]/.test(field);

        if (needsQuoting) {
            return `"${this.escapeCsvField(field)}"`;
        }

        return field;
    }

    /**
     * Format date as YYYY-MM-DD in local timezone
     * 
     * @param date - Date object
     * @returns ISO date string (local timezone)
     */
    private static formatLocalDate(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    /**
     * Trigger browser download for a file
     * 
     * Uses the Browser File API (Blob + URL.createObjectURL)
     * No external dependencies required
     * 
     * @param content - File content
     * @param filename - Download filename
     * @param mimeType - MIME type for the file
     */
    private static downloadFile(
        content: string,
        filename: string,
        mimeType: string
    ): void {
        // Create blob with UTF-8 encoding
        const blob = new Blob([content], { type: mimeType });

        // Create temporary URL
        const url = URL.createObjectURL(blob);

        // Create temporary link element
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';

        // Add to DOM, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up URL
        URL.revokeObjectURL(url);
    }

    /**
     * Get export statistics for preview
     * 
     * @param expenses - Array of expenses
     * @returns Statistics about the export
     */
    static getExportStats(expenses: Expense[]): {
        totalRecords: number;
        dateRange: { earliest: string; latest: string } | null;
        totalAmount: number;
        currencies: string[];
    } {
        if (expenses.length === 0) {
            return {
                totalRecords: 0,
                dateRange: null,
                totalAmount: 0,
                currencies: []
            };
        }

        const dates = expenses.map(e => e.date).sort();
        const currencies = [...new Set(expenses.map(e => e.currency))];
        const totalAmount = expenses.reduce((sum, e) => sum + e.amount, 0);

        return {
            totalRecords: expenses.length,
            dateRange: {
                earliest: dates[0],
                latest: dates[dates.length - 1]
            },
            totalAmount,
            currencies
        };
    }
}
